import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import LoadingScreen from 'src/componentes/LoadingScreen';

let keyCount = 0;
const instances = {};

const ContentWrapper = React.memo(
  function ContentWrapper({ content, ...other }) {
    return (
      <div>
        <Suspense fallback={<LoadingScreen />}>
          {React.cloneElement(content, other)}
        </Suspense>
      </div>
    );
  },
  (previousProps, nextProps) =>
    previousProps.content === nextProps.content &&
    previousProps.index === nextProps.index
);

function ContentManager({ name = 'default' }) {
  const [contents, setContents] = useState([]);

  const removeContent = useCallback((key) => {
    setContents((state) => state.filter((content) => content.key !== key));
  }, []);

  const extendContent = useCallback(
    (content, key) =>
      React.cloneElement(content, {
        remove: () => {
          removeContent(key);
        },
      }),
    [removeContent]
  );

  const addContent = useCallback(
    (content, options = {}) => {
      const key = (++keyCount).toString();
      const { persist = false } = options;

      setContents((state) =>
        state.concat([
          {
            key,
            persist,
            content: extendContent(content, key),
          },
        ])
      );

      return key;
    },
    [extendContent]
  );

  const updateContent = useCallback(
    (key, content) => {
      setContents((state) =>
        state.map((holder) =>
          holder.key === key
            ? { ...holder, content: extendContent(content, key) }
            : holder
        )
      );
    },
    [extendContent]
  );

  const removeAllContents = useCallback((options = {}) => {
    const { includePersistent = false } = options;
    setContents((state) =>
      includePersistent ? [] : state.filter((holder) => holder.persist)
    );
  }, []);

  const instance = useMemo(
    () => ({
      addContent,
      updateContent,
      removeContent,
      removeAllContents,
    }),
    [addContent, updateContent, removeContent, removeAllContents]
  );

  useEffect(() => {
    instances[name] = instance;

    return () => {
      if (instances[name] === instance) {
        delete instances[name];
      }
    };
  }, [instance, name]);

  return (
    <div>
      {contents.map((holder, index) => (
        <ContentWrapper
          key={holder.key}
          content={holder.content}
          index={index}
        />
      ))}
    </div>
  );
}

ContentManager.propTypes = {
  name: PropTypes.string.isRequired,
};

ContentManager.instances = instances;

ContentManager.addContent = (
  content,
  { target = 'default2', persist = false } = {}
) => ContentManager.instances[target].addContent(content, { persist });

ContentManager.updateContent = (key, newContent, target = `default2`) => {
  ContentManager.instances[target].updateContent(key, newContent);
};

ContentManager.removeContent = (key, target = `default2`) => {
  ContentManager.instances[target].removeContent(key);
};

ContentManager.removeAllContentsByTarget = (
  target = 'default2',
  { includePersistent = false } = {}
) => {
  if (!ContentManager.instances?.[target]) return;
  ContentManager.instances[target].removeAllContents({ includePersistent });
};

export default ContentManager;
